import { useEffect, useRef, useState } from 'react'
import { COLLAB_FILE_DELETED_EVENT } from '@papan/collab/client/session-events'

/** The server's answer for the share session's file: back (200), still gone (in the bin), or the session is over. */
export type ShareFileAnswer = 'back' | 'gone' | 'stop'

/** While the editor is ended, how often the share view asks again (it has no realtime restore signal). */
export const SHARE_RECOVERY_INTERVAL_MS = 20_000

/**
 * #823 — a share-link visitor's editor whose session the server ended because the file went to the bin (collab 4410)
 * comes back once the file is back. Only 4410: a 4403 is a refusal (access withdrawn, the link revoked) that /info does
 * not judge — it would answer 200 and every fresh editor be refused again, a remount loop. The share view has no realtime channel, so while the editor is ended it
 * asks `check` — at once, on focus / visibility / online / bfcache restore, and every `intervalMs` — and on "back"
 * returns a new remount key (a fresh editor joins the room again; the ended one never reconnects). "stop" (the
 * session itself is over) ends the asking: the share view's own front-door handling deals with that.
 * Returns the remount counter to fold into the editor's key.
 */
export function useShareEditorRecovery(opts: {
  liveRoom: string
  check: () => Promise<ShareFileAnswer>
  intervalMs?: number
}): number {
  const { liveRoom, intervalMs = SHARE_RECOVERY_INTERVAL_MS } = opts
  const checkRef = useRef(opts.check)
  checkRef.current = opts.check
  const [remount, setRemount] = useState(0)
  const [ended, setEnded] = useState(false)

  // the editor's own end, matched by the room it joined
  useEffect(() => {
    if (!liveRoom) return
    const onEnd = (e: Event) => {
      if ((e as CustomEvent<{ room?: string }>).detail?.room === liveRoom) setEnded(true)
    }
    window.addEventListener(COLLAB_FILE_DELETED_EVENT, onEnd)
    return () => window.removeEventListener(COLLAB_FILE_DELETED_EVENT, onEnd)
  }, [liveRoom])

  // a new room is a new editor: nothing ended yet
  useEffect(() => { setEnded(false) }, [liveRoom])

  useEffect(() => {
    if (!ended) return
    let live = true
    let inFlight = false
    const ask = () => {
      if (!live || inFlight) return
      inFlight = true
      void checkRef.current().then(answer => {
        if (!live) return
        // "back" ends this asking AT ONCE: a timer / focus tick between this answer and React's re-render (whose cleanup
        // stops the effect) would otherwise ask again and remount the editor twice
        if (answer === 'back') { live = false; clearInterval(timer); setEnded(false); setRemount(n => n + 1) }
        else if (answer === 'stop') { live = false; clearInterval(timer) }
      }, () => { /* no answer: asked again on the next trigger */ }).finally(() => { inFlight = false })
    }
    const onVisible = () => { if (document.visibilityState === 'visible') ask() }
    const timer = setInterval(ask, intervalMs)
    window.addEventListener('focus', ask)
    window.addEventListener('online', ask)
    window.addEventListener('pageshow', ask)
    document.addEventListener('visibilitychange', onVisible)
    ask()
    return () => {
      live = false
      clearInterval(timer)
      window.removeEventListener('focus', ask)
      window.removeEventListener('online', ask)
      window.removeEventListener('pageshow', ask)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [ended, intervalMs])

  return remount
}
